import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, Plus, Settings, Zap, LogOut, Copy, Check, Users, Building2, Shield, ChevronDown, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import QRCodeDialog from '@/components/QRCodeDialog';
import SubscriptionInfo from '@/components/SubscriptionInfo';
import AgentWizard from '@/components/AgentWizard';
import api from '@/utils/api';
import { toast } from 'sonner';

const TORTLAB_LOGO = "https://customer-assets.emergentagent.com/job_35f2eb62-f2f6-4b48-9388-e2c1ee261a61/artifacts/tfwz2zen_generated-image-1%20%284%29.png";

export default function Dashboard({ user }) {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedToken, setCopiedToken] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [wizardAgentId, setWizardAgentId] = useState(null);

  useEffect(() => {
    fetchAgents();
    if (user?.role === 'PlatformAdmin') {
      fetchTenants();
    }
  }, [user?.role]);

  const fetchAgents = async () => {
    try {
      const response = await api.get('/agents');
      setAgents(response.data);
    } catch (error) {
      toast.error('Failed to fetch agents');
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await api.get('/admin/tenants');
      // API returns {tenants: [...]} or array directly
      const tenantsData = response.data?.tenants || response.data || [];
      setTenants(Array.isArray(tenantsData) ? tenantsData : []);
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
    }
  };

  const handleWizardComplete = (agentId, finished) => {
    setWizardAgentId(agentId);
    if (finished) {
      fetchAgents();
    }
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
      localStorage.removeItem('auth_token');
      navigate('/login');
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const copyPublicUrl = async (token) => {
    const url = `${window.location.origin}/chat/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedToken(token);
      toast.success('Public URL copied to clipboard!');
      setTimeout(() => setCopiedToken(null), 2000);
    } catch (error) {
      // Fallback for browsers without clipboard permission
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedToken(token);
        toast.success('Public URL copied to clipboard!');
        setTimeout(() => setCopiedToken(null), 2000);
      } catch (err) {
        toast.error('Failed to copy URL. Please copy manually: ' + url);
      }
      document.body.removeChild(textArea);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200/50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src={TORTLAB_LOGO} 
                alt="TortLab" 
                className="h-10 object-contain"
                data-testid="header-logo"
              />
              <div>
                <h1 className="text-xl font-heading font-bold text-foreground" data-testid="dashboard-title">TortLab</h1>
                <p className="text-sm text-muted-foreground">{user?.name}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {user?.role === 'PlatformAdmin' && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="rounded-full" data-testid="admin-menu-btn">
                      <Shield className="w-4 h-4 mr-2" />
                      Admin
                      <ChevronDown className="w-4 h-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => navigate('/admin/panel')}>
                      <Building2 className="w-4 h-4 mr-2" />
                      Manage Accounts
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/admin/service-queue')}>
                      <Users className="w-4 h-4 mr-2" />
                      Service Queue
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/admin/system-settings')}>
                      <Settings className="w-4 h-4 mr-2" />
                      System Settings
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="rounded-full"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats and Subscription Row */}
        <div className={`grid grid-cols-1 gap-6 mb-8 ${user?.role === 'PlatformAdmin' ? 'md:grid-cols-3' : 'lg:grid-cols-4'}`}>
          {/* Stats Cards */}
          <Card className="border-border shadow-sm hover:shadow-md transition-shadow" data-testid="stats-agents">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Total Agents</p>
                  <p className="text-3xl font-heading font-bold text-foreground mt-2">{agents.length}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Bot className="w-6 h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border shadow-sm hover:shadow-md transition-shadow" data-testid="stats-active">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Active Agents</p>
                  <p className="text-3xl font-heading font-bold text-foreground mt-2">
                    {agents.filter((a) => a.is_active).length}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-secondary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Role</p>
                  <p className="text-lg font-semibold text-foreground mt-2">{user?.role}</p>
                </div>
                <Badge variant="secondary" className="text-xs">{user?.role}</Badge>
              </div>
            </CardContent>
          </Card>

          {/* Subscription Info - Only show for ClientAdmin and AgentAdmin, not PlatformAdmin */}
          {user?.role !== 'PlatformAdmin' && (
            <SubscriptionInfo userRole={user?.role} />
          )}
        </div>

        {/* Tenants Section - Only for PlatformAdmin */}
        {user?.role === 'PlatformAdmin' && tenants.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-heading font-bold text-foreground">Tenants</h2>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin/panel')}
                className="rounded-full"
              >
                <Building2 className="w-4 h-4 mr-2" />
                Manage All
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tenants.slice(0, 6).map((tenant) => (
                <Card 
                  key={tenant.tenant_id} 
                  className="border-border shadow-sm hover:shadow-md transition-all cursor-pointer hover:-translate-y-0.5"
                  onClick={() => navigate('/admin/panel')}
                  data-testid={`tenant-card-${tenant.tenant_id}`}
                >
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-bold">
                          {tenant.name?.charAt(0).toUpperCase() || 'T'}
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground">{tenant.name}</h3>
                          <p className="text-sm text-muted-foreground">
                            {tenant.user_count || 0} users • {tenant.agent_count || 0} agents
                          </p>
                        </div>
                      </div>
                      <Badge variant={tenant.status === 'active' ? 'default' : 'secondary'} className="text-xs">
                        {tenant.status || 'active'}
                      </Badge>
                    </div>
                    {tenant.subscription_tier && (
                      <div className="mt-3 pt-3 border-t border-border">
                        <p className="text-xs text-muted-foreground">
                          Plan: <span className="font-medium text-foreground">{tenant.subscription_tier}</span>
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
            {tenants.length > 6 && (
              <p className="text-sm text-muted-foreground text-center mt-4">
                +{tenants.length - 6} more tenants. <button onClick={() => navigate('/admin/panel')} className="text-primary hover:underline">View all</button>
              </p>
            )}
          </div>
        )}

        {/* Agents List */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-heading font-bold text-foreground">Your Agents</h2>
          {user?.role !== 'AgentAdmin' && (
            <Button
              onClick={() => {
                setWizardAgentId(null);
                setShowWizard(true);
              }}
              className="rounded-full shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all hover:-translate-y-0.5"
              data-testid="create-agent-button"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Create Agent
            </Button>
          )}
        </div>

          {agents.length === 0 ? (
            <Card className="border-dashed border-2 border-border" data-testid="empty-state">
              <CardContent className="py-16 text-center">
                <Bot className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-heading font-semibold text-foreground mb-2">No agents assigned</h3>
                <p className="text-muted-foreground mb-6">
                  {user?.role === 'AgentAdmin' 
                    ? 'Contact your administrator to get agents assigned to you'
                    : 'Create your first AI chat agent to get started'}
                </p>
                {user?.role !== 'AgentAdmin' && (
                  <Button
                    onClick={() => {
                      setWizardAgentId(null);
                      setShowWizard(true);
                    }}
                    className="rounded-full"
                    data-testid="empty-create-button"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Your First Agent
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="agents-list">
            {agents.map((agent) => (
              <Card
                key={agent.agent_id}
                className="border-border shadow-sm hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 cursor-pointer hover:border-primary/20"
                data-testid={`agent-card-${agent.agent_id}`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg font-heading" data-testid={`agent-name-${agent.agent_id}`}>{agent.name}</CardTitle>
                      <CardDescription className="mt-1">
                        Response: {agent.response_style}
                      </CardDescription>
                    </div>
                    <Badge variant={agent.is_active ? 'default' : 'secondary'} data-testid={`agent-status-${agent.agent_id}`}>
                      {agent.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {agent.context_text}
                  </p>

                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyPublicUrl(agent.public_agent_token)}
                      className="flex-1 rounded-full"
                      data-testid={`copy-url-${agent.agent_id}`}
                    >
                      {copiedToken === agent.public_agent_token ? (
                        <Check className="w-3 h-3 mr-1" />
                      ) : (
                        <Copy className="w-3 h-3 mr-1" />
                      )}
                      {copiedToken === agent.public_agent_token ? 'Copied!' : 'Copy URL'}
                    </Button>
                    <QRCodeDialog agentId={agent.agent_id} agentName={agent.name} />
                  </div>

                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/agents/${agent.agent_id}/settings`)}
                      className="flex-1 rounded-full"
                      data-testid={`settings-${agent.agent_id}`}
                    >
                      <Settings className="w-3 h-3 mr-1" />
                      Settings
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/agents/${agent.agent_id}/buttons`)}
                      className="flex-1 rounded-full"
                      data-testid={`buttons-${agent.agent_id}`}
                    >
                      <Zap className="w-3 h-3 mr-1" />
                      Buttons
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Agent Creation/Edit Wizard */}
      <AgentWizard
        isOpen={showWizard}
        onClose={() => {
          setShowWizard(false);
          setWizardAgentId(null);
        }}
        agentId={wizardAgentId}
        onComplete={handleWizardComplete}
      />
    </div>
  );
}
